import { useState } from 'react'
import type { FormEvent } from 'react'
import { useSidTitel } from '../lib/seo'

type Skickstatus = 'redo' | 'skickar' | 'skickat' | 'fel' | 'inaktiv'

/** Kontaktformuläret postar till /api/kontakt — en Worker med rutt på
 *  ledarskap.net (workers/kontakt/) som mejlar till info@ledarskap.net via
 *  Cloudflare Email Service. Fältet "webbplats" är en honeypot: människor ser
 *  det inte, spamrobotar fyller i det — sådana anrop släpps tyst. */
function Kontaktformular() {
  const [status, setStatus] = useState<Skickstatus>('redo')

  const skicka = async (handelse: FormEvent<HTMLFormElement>) => {
    handelse.preventDefault()
    const formular = handelse.currentTarget
    const data = Object.fromEntries(new FormData(formular))
    setStatus('skickar')
    try {
      const svar = await fetch('/api/kontakt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (svar.ok) {
        formular.reset()
        setStatus('skickat')
      } else if (svar.status === 503) {
        setStatus('inaktiv')
      } else {
        setStatus('fel')
      }
    } catch {
      setStatus('fel')
    }
  }

  return (
    <form className="kontaktformular no-print" onSubmit={skicka}>
      <label>
        Din e-postadress
        <input type="email" name="epost" required autoComplete="email" />
      </label>
      <label>
        Meddelande
        <textarea name="meddelande" required minLength={10} rows={6} />
      </label>
      <label className="honeypot" aria-hidden="true" tabIndex={-1}>
        Webbplats
        <input type="text" name="webbplats" tabIndex={-1} autoComplete="off" />
      </label>
      <button type="submit" className="knapp knapp-primar" disabled={status === 'skickar'}>
        {status === 'skickar' ? 'Skickar …' : 'Skicka'}
      </button>
      {status === 'skickat' && <p className="formular-status formular-ok" role="status">Tack! Ditt meddelande har skickats.</p>}
      {status === 'fel' && (
        <p className="formular-status formular-fel" role="status">Något gick fel — försök igen om en stund.</p>
      )}
      {status === 'inaktiv' && (
        <p className="formular-status formular-fel" role="status">
          Kontaktformuläret är inte aktiverat ännu. Försök gärna igen senare.
        </p>
      )}
    </form>
  )
}

/** Om-sidan: vad läromedlet är, hur det tagits fram (inklusive den
 *  AI-transparens EU:s AI-förordning kräver) och en kontaktväg.
 *  Texten är ett grundförslag avsett att justeras för hand. */
export default function Om() {
  useSidTitel('Om läromedlet')

  return (
    <div className="wrap">
      <article className="dokument">
        <h1>Om läromedlet</h1>

        <p>
          Ledarskap och organisation är ett fritt, digitalt läromedel för gymnasiekursen
          Ledarskap och organisation, nivå 1 (Gy25, ämneskod LEDA). Det omfattar elva kapitel
          med lärotext, begreppslistor, instuderingsfrågor, övningsuppgifter och quiz, samt ett
          komplett lärarmaterial med lektionsplaneringar, bildspel, arbetsblad och prov.
          Läromedlet är ett fristående projekt, fritt att använda i undervisning under
          licensen som beskrivs längst ned på sidan.
        </p>

        <h2>Så har läromedlet tagits fram</h2>
        <p>
          Innehållet bygger uteslutande på Skolverkets ämnesplan för LEDA och på etablerade
          källor inom organisationsteori, psykologi och ledarskapsforskning. Varje sakpåstående
          i lärotexten är spårbart till en namngiven källa i ett källregister med
          evidensnivåer, och allt innehåll går genom en granskningskedja i tre steg —
          faktagranskning mot källorna, pedagogisk granskning mot kursplanen och
          språkgranskning — innan det godkänns manuellt och publiceras.
        </p>

        <h2>Innehållet är framtaget med AI</h2>
        <p>
          Texter, övningar, lektionsplaneringar och illustrationer har genererats med hjälp av
          artificiell intelligens (stora språkmodeller) och därefter kvalitetssäkrats enligt
          processen ovan, med mänskligt godkännande som sista steg. Den här informationen
          lämnas i enlighet med EU:s AI-förordning (AI Act) och dess krav på transparens om
          AI-genererat innehåll. Hittar du något som verkar felaktigt? Hör gärna av dig via
          formuläret nedan.
        </p>

        <h2>Licens</h2>
        <p>
          Läromedlet är licensierat under{' '}
          <a
            href="https://creativecommons.org/licenses/by-nc-sa/4.0/deed.sv"
            target="_blank"
            rel="license noopener"
          >
            Creative Commons Erkännande-IckeKommersiell-DelaLika 4.0 Internationell
            (CC BY-NC-SA 4.0)
          </a>
          . Det innebär att du fritt får använda, kopiera och bearbeta materialet i
          undervisning, så länge du anger upphovspersonen, inte använder det kommersiellt och
          delar eventuella bearbetningar under samma licens.
        </p>
        <a
          className="licensmarke"
          href="https://creativecommons.org/licenses/by-nc-sa/4.0/deed.sv"
          target="_blank"
          rel="license noopener"
          aria-label="Licensvillkor: Creative Commons Erkännande-IckeKommersiell-DelaLika 4.0 Internationell"
        >
          <img src="/licens/cc.svg" alt="" />
          <img src="/licens/by.svg" alt="" />
          <img src="/licens/nc.svg" alt="" />
          <img src="/licens/sa.svg" alt="" />
        </a>

        <h2>Kontakt</h2>
        <p>Frågor, synpunkter eller felrapporter — skriv en rad så återkommer vi.</p>
        <Kontaktformular />
      </article>
    </div>
  )
}
